import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Grid,
  NumberInput,
  MultiSelect,
  Button,
  TextInput,
  Loader,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDebouncedCallback, useDisclosure } from '@mantine/hooks';
import DetailsModal from '@/components/DetailsModal';
import validations from '@/helpers/validation';
import paymentProofMutations from '@/api/mutitations/paymentProof';
import agentMutations from '@/api/mutitations/agent';
import s3Mutations from '@/api/mutitations/s3';
import { notifications } from '@mantine/notifications';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';
import Modal from '../Modal/Index';

const EditPaymentProof = (props) => {
  const {
    open, close, opened, id, setKey,
  } = props;

  const t = prepareTranslate();
  const {
    mutate: getPaymentProofById,
    data: paymentProofData,
  } = paymentProofMutations.useGetPaymentProofById();
  const { mutateAsync: agentMutate } = agentMutations.useGetAgents();
  const { mutateAsync: updatePaymentProofMutation } = paymentProofMutations.useUpdatePaymentProof();
  const [submitLoadingOpened, { open: openSubmit, close: closeSubmit }] = useDisclosure();
  const [loadingOpened, { open: openLoading, close: closeLoading }] = useDisclosure();
  const [actionResultOpened, {
    open: actionResultOpen, close: actionResultClose,
  }] = useDisclosure(false);

  useEffect(() => {
    if (opened && id) {
      getPaymentProofById({ data: { id } });
    }
  }, [opened, id, getPaymentProofById]);

  const [data, setData] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [resultLabel, setResultLabel] = useState('');

  const { mutateAsync: uploadMutate } = s3Mutations.useUpload();
  const { mutateAsync: deleteMutate } = s3Mutations.useDelete();

  const form = useForm({
    initialValues: {
      paymentProofNumber: '',
      paymentDate: null,
      associatedAgents: [],
      value: '',
      files: [],
      comments: '',
    },
    validate: {
      paymentProofNumber: (val) => validations.required(val),
      paymentDate: (val) => validations.required(val),
      associatedAgents: (val) => validations.required(val),
      value: (val) => {
        if (!val) return null;
        return validations.number(val);
      },
    },
  });

  useEffect(() => {
    if (!paymentProofData || !paymentProofData.paymentProof) return;
    const {
      associatedAgents, paymentProofNumber, paymentDate, value, files, comments,
    } = paymentProofData.paymentProof;

    const options = associatedAgents?.map((agent) => ({
      label: agent.personalDetails.fullName,
      value: agent._id,
    }));
    if (options && options.length > 0) {
      setData((prev) => {
        const merged = [...prev, ...options];
        return merged.filter(
          (opt, index, self) => (
            index === self.findIndex((o) => o.value === opt.value)),
        );
      });
    }

    form.setValues({
      paymentProofNumber,
      paymentDate: paymentDate ? new Date(paymentDate) : null,
      associatedAgents: associatedAgents?.map((agent) => agent._id) || [],
      value: value || '',
      files: files || [],
      comments: comments || '',
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProofData]);

  const autoCompleteQuery = useDebouncedCallback(async (val) => {
    openLoading();
    if (val === '') {
      closeLoading();
      return;
    }
    const start = 1;
    const query = { 'personalDetails.fullName': val };

    try {
      const { data: agentData } = await agentMutate({ query, start });
      if (agentData.length !== 0) {
        const options = agentData.map((agent) => ({
          label: agent.personalDetails.fullName,
          value: agent._id,
        }));
        setData((prev) => {
          const merged = [...prev, ...options];

          return merged.filter((opt, index, self) => (
            index === self.findIndex((o) => o.value === opt.value)
          ));
        });
      }
    } catch {
      notifications.show({
        title: t('pages.reports.notification.error.title'),
        message: t('pages.reports.notification.error.message'),
        color: 'red',
      });
    }

    closeLoading();
  }, 500);

  const handleAutocompleteChange = (val) => {
    if (!data.find((agent) => agent.label === val)) {
      autoCompleteQuery(val);
    }
  };

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
    const fileToRemove = form.values.files[index];

    if (fileToRemove?.path) {
      setFilesToDelete((prev) => [...prev, fileToRemove]);
    }
  };

  const handleUpdateClick = async (values) => {
    openSubmit();
    try {
      filesToDelete.forEach((file) => {
        const { path } = file;
        deleteMutate({ path });
      });

      const newFiles = form.values.files?.filter((f) => !f.path);
      const clonedValues = { ...values, id };

      if (newFiles?.length) {
        const formData = new FormData();
        newFiles.forEach((f) => formData.append('files', f.file, f.name));
        const uploadedFiles = await uploadMutate(formData);
        const oldFiles = form.values.files.filter((f) => f.path);
        clonedValues.files = [...uploadedFiles.files, ...oldFiles];
      }
      await updatePaymentProofMutation(clonedValues);

      setResultLabel(t('components.paymentProofEdit.notification.success.title'));
      actionResultOpen();
      close();
      setKey((prev) => prev + 1);
    } catch {
      setResultLabel(t('components.paymentProofEdit.notification.failure.title'));
      actionResultOpen();
    } finally {
      closeSubmit();
    }
  };

  const handleCancelClick = () => {
    form.errors = null;
    close();
  };

  const actions = [
    <Button key="primary" w="7rem" loading={submitLoadingOpened} onClick={form.onSubmit(handleUpdateClick)}>
      {t('global.update')}
    </Button>,
    <Button key="cancel" w="7rem" variant="outline" onClick={handleCancelClick}>
      {t('global.cancel')}
    </Button>,
  ];

  return (
    <>
      <DetailsModal actions={actions} open={open} close={handleCancelClick} opened={opened} label={t('components.paymentProofEdit.title')}>
        <Grid gutter="xl" mt={16}>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <TextInput required label={t('components.paymentProofEdit.paymentProofNumber')} {...form.getInputProps('paymentProofNumber')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <NumberInput hideControls label={t('components.paymentProofEdit.value')} {...form.getInputProps('value')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <DatePickerInput required maxDate={new Date()} label={t('components.paymentProofEdit.paymentDate')} {...form.getInputProps('paymentDate')} />
          </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
            <TextInput
              label={t('components.paymentProofEdit.comments')}
              {...form.getInputProps('comments')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 12 }}>
            <MultiSelect
              required
              searchable
              rightSection={loadingOpened ? <Loader size="1rem" /> : null}
              onSearchChange={handleAutocompleteChange}
              data={data}
              label={t('components.paymentProofEdit.associatedAgents')}
              {...form.getInputProps('associatedAgents')}
            />
          </Grid.Col>
        </Grid>
        {form.values.files?.length > 0 && (
          <EditFiles
           files={form.values.files}
           onNameChange={onFileNameChange}
           onRemove={onDeleteFile} />
        )}
          <UploadFilesButton onFilesSelected={onFileSelected} label={t('components.paymentProofEdit.uploadFiles')} />
      </DetailsModal>

      <Modal opened={actionResultOpened} onClose={actionResultClose} actions={[
        <Button key="closeButton" onClick={actionResultClose}>{t('global.close')}</Button>,
      ]} label={resultLabel} />
    </>
  );
};

EditPaymentProof.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  id: PropTypes.string,
  setKey: PropTypes.func,
};

export default EditPaymentProof;
